import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import type {
  ArchiveEvent,
  ArchiveEventNotifier,
} from "@/features/archive/mediators/archiveEvents";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { useComponentNode } from "@/shared/ui/component-hierarchy/hooks";

type ArchiveStorageDisclosureProps = {
  readonly children: React.ReactNode;
  readonly isExpanded: boolean;
};

type ArchiveStorageDisclosureViewProps = ArchiveStorageDisclosureProps & {
  readonly onEvent: ArchiveEventNotifier;
};

const DETAILS_ID = "archive-storage-disclosure-details";
const HEADING_ID = "archive-storage-disclosure-heading";

/** 板別容量表示の状態とイベント階層をViewへ接続するPresenter。 */
export const ArchiveStorageDisclosure: React.FunctionComponent<
  ArchiveStorageDisclosureProps
> = ({ children, isExpanded }: ArchiveStorageDisclosureProps) => {
  const node = useComponentNode();
  const notify: ArchiveEventNotifier = (
    event: ArchiveEvent,
  ): ReturnType<ArchiveEventNotifier> => node.dispatch(event).status;

  return (
    <ComponentScope node={node}>
      <ArchiveStorageDisclosureView isExpanded={isExpanded} onEvent={notify}>
        {children}
      </ArchiveStorageDisclosureView>
    </ComponentScope>
  );
};

/** 過去ログ容量の開閉を表示し、利用者の入力をイベントで通知するView。 */
export const ArchiveStorageDisclosureView: React.FunctionComponent<
  ArchiveStorageDisclosureViewProps
> = ({ children, isExpanded, onEvent }: ArchiveStorageDisclosureViewProps) => (
  <section
    aria-labelledby={HEADING_ID}
    className="archive-storage-disclosure m-2 flex flex-col gap-2 rounded-lg border border-border bg-card px-3 py-1 text-card-foreground"
  >
    <header className="archive-storage-disclosure-header flex min-h-11 items-center justify-between gap-2">
      <h2 className="m-0 text-sm font-medium text-foreground" id={HEADING_ID}>
        過去ログ容量
      </h2>
      <button
        aria-controls={DETAILS_ID}
        aria-expanded={isExpanded}
        className="archive-storage-disclosure-toggle inline-flex min-h-10 shrink-0 items-center gap-1 rounded-md px-2 text-sm font-medium text-primary hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        onClick={(): void => {
          onEvent({ type: "archive/storage-disclosure-toggle-requested" });
        }}
        type="button"
      >
        {isExpanded ? "詳細を閉じる" : "詳細を表示"}
        {isExpanded ? (
          <FiChevronUp aria-hidden="true" />
        ) : (
          <FiChevronDown aria-hidden="true" />
        )}
      </button>
    </header>
    <div
      className="archive-storage-disclosure-panel"
      hidden={!isExpanded}
      id={DETAILS_ID}
    >
      <div className="archive-storage-disclosure-grid">{children}</div>
    </div>
  </section>
);
